/** How often, and for how long, the dashboard checks on a background video send. */
const VIDEO_SEND_POLL_MS = 3_000;
const VIDEO_SEND_GIVE_UP_MS = 5 * 60_000;

/** What the rep is told when the watch ends with no answer: never a failure — the worker may still send. */
export const VIDEO_SEND_STILL_SENDING_MESSAGE = 'Still sending — check the conversation before resending.';

export type VideoNoteSendStatus = { state: 'sending' | 'sent' | 'failed' | 'unknown'; message?: string };

/**
 * Wait for a background video send to finish: `'sent'` once it's sent, a rejection with the reason only
 * when the worker reported a failure (so the toast says why), and `'still_sending'` when five minutes
 * pass without an answer. A status read that errors (a network blip, a deploy) is not an answer — the
 * worker is still sending, and reporting a failure would invite a resend — so it keeps watching.
 */
export async function waitForVideoNoteSend(
  readStatus: (jobId: string) => Promise<VideoNoteSendStatus>,
  jobId: string,
  timing: { pollMs: number; giveUpMs: number } = { pollMs: VIDEO_SEND_POLL_MS, giveUpMs: VIDEO_SEND_GIVE_UP_MS },
): Promise<'sent' | 'still_sending'> {
  const deadline = Date.now() + timing.giveUpMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, timing.pollMs));
    let status: VideoNoteSendStatus;
    try {
      status = await readStatus(jobId);
    } catch {
      continue;
    }
    if (status.state === 'sent') return 'sent';
    if (status.state === 'failed') throw new Error(status.message ?? 'The video could not be sent.');
  }
  return 'still_sending';
}
